<template>
  <SatellitesPage :title="title" :back="{ name: 'Satellites' }" :back-label="$t('satellites.tabs.satellites')">
    <template v-if="satellite" #title-extra>
      <SatelliteStatus :status="satellite.status" />
    </template>

    <div v-if="loading" class="d-flex justify-content-center py-5">
      <img :src="require('@/assets/images/rolling.svg')" alt="loader" class="loader" />
    </div>

    <div v-else-if="satellite" class="satellite-layout">
      <div class="satellite-main">
        <!-- Name and group -->
        <section class="sat-card">
          <h2 class="sat-card__title">{{ $t("satellites.page.general") }}</h2>
          <div class="satellite-general">
            <div class="sat-field">
              <label class="sat-field__label" for="satellite-name">{{ $t("satellites.columns.name") }}</label>
              <input id="satellite-name" v-model="form.name" type="text" maxlength="64" :placeholder="satellite.hostname || $t('satellites.name_placeholder')" :class="{ invalid: !!violation('name') }" />
              <div v-if="violation('name')" class="sat-field__error">{{ violation("name") }}</div>
            </div>
            <div class="sat-field">
              <span class="sat-field__label">{{ $t("satellites.columns.group") }}</span>
              <GroupSelect v-model="form.groupId" :groups="groups" />
            </div>
          </div>
          <div v-if="groupChangeNote" class="sat-note mt-3" :class="{ _warn: groupChangeNote.warn }">
            <SatIcon :name="groupChangeNote.warn ? 'alert' : 'info'" />
            <span>{{ groupChangeNote.text }}</span>
          </div>
        </section>

        <!-- Settings: the group's or its own -->
        <section class="sat-card">
          <template v-if="form.groupId">
            <h2 class="sat-card__title">
              {{ $t("satellites.page.settings") }}
              <span class="sat-chip _accent"><SatIcon name="layers" :size="12" /> {{ groupName(form.groupId) }}</span>
            </h2>
            <p class="sat-card__subtitle">
              {{ $t("satellites.page.settings_by_group") }}
              <router-link :to="{ name: 'SatelliteGroup', params: { id: form.groupId } }" class="sat-link">{{ $t("satellites.page.open_group") }}</router-link>
            </p>
            <div v-if="!groupSettings[form.groupId]" class="d-flex justify-content-center py-3">
              <img :src="require('@/assets/images/rolling.svg')" alt="loader" class="loader" />
            </div>
            <SettingsForm v-else :definitions="definitions" :value="groupSettings[form.groupId]" readonly />
          </template>
          <template v-else>
            <h2 class="sat-card__title">{{ $t("satellites.page.settings") }}</h2>
            <p class="sat-card__subtitle">{{ $t("satellites.page.settings_own") }}</p>
            <SettingsForm v-model="form.settings" :definitions="definitions" :violations="violations" :disabled="saving" />
          </template>
        </section>

        <!-- Domains -->
        <section class="sat-card">
          <h2 class="sat-card__title">
            {{ $t("satellites.page.domains") }}
            <span class="sat-chip">{{ domains.length }}</span>
          </h2>
          <p class="sat-card__subtitle">{{ $t("satellites.page.domains_hint") }}</p>
          <DomainList :domains="domains" />
          <router-link :to="{ name: 'SatelliteDomains', query: satellite.group ? { group: satellite.group.id } : {} }" class="sat-link satellite-manage">
            {{ $t("satellites.page.manage_domains") }} <SatIcon name="chevron-right" :size="14" />
          </router-link>
        </section>

        <SaveBar :dirty="dirty" :saving="saving" :invalid="invalid" @save="save" @discard="reset" />
      </div>

      <!-- Server -->
      <aside class="satellite-side">
        <section class="sat-card">
          <h2 class="sat-card__title">{{ $t("satellites.page.server") }}</h2>
          <dl class="satellite-facts">
            <div>
              <dt>IP</dt>
              <dd>
                <template v-if="satellite.ip">
                  <span class="sat-mono">{{ satellite.ip }}</span>
                  <button type="button" class="sat-icon-btn satellite-copy" :title="$t('satellites.links.copy')" @click="copy(satellite.ip)"><SatIcon :name="copied ? 'check' : 'copy'" :size="14" /></button>
                </template>
                <span v-else class="sat-muted">{{ $t("satellites.awaiting_install") }}</span>
              </dd>
            </div>
            <div v-if="satellite.hostname">
              <dt>{{ $t("satellites.page.hostname") }}</dt>
              <dd class="sat-mono">{{ satellite.hostname }}</dd>
            </div>
            <div>
              <dt>{{ $t("satellites.columns.config") }}</dt>
              <dd><ConfigStatus v-if="satellite.installed_at" :status="satellite.config_status" /><span v-else class="sat-muted">—</span></dd>
            </div>
            <div v-if="satellite.status === 'online' && satellite.cpu_load">
              <dt>{{ $t("satellites.columns.cpu") }}</dt>
              <dd><CpuLoadChart :load="satellite.cpu_load" /></dd>
            </div>
            <div>
              <dt>{{ $t("satellites.columns.agent_version") }}</dt>
              <dd>{{ satellite.agent_version || "—" }}<AgentUpdateBadge :update="satellite.agent_update" /></dd>
            </div>
            <div>
              <dt>{{ $t("satellites.columns.os") }}</dt>
              <dd>{{ [satellite.os, satellite.os_version, satellite.arch].filter(Boolean).join(" · ") || "—" }}</dd>
            </div>
            <div>
              <dt>{{ $t("satellites.columns.installed_at") }}</dt>
              <dd>{{ formatDate(satellite.installed_at) }}</dd>
            </div>
            <div>
              <dt>{{ $t("satellites.columns.last_heartbeat") }}</dt>
              <dd>{{ formatDate(satellite.last_heartbeat_at) }}</dd>
            </div>
          </dl>
          <button v-if="satellite.install_command" type="button" class="btn _border _green w-100 mt-3" @click="$modal.show('satelliteInstall')">{{ $t("satellites.install_command") }}</button>
          <button v-if="canRequestAgentUpdate(satellite)" type="button" class="btn _border _green w-100 mt-3" @click="$modal.show('satelliteAgentUpdate')">
            {{ $t("satellites.update.button_version", { version: satellite.agent_update.latest_version }) }}
          </button>
        </section>
        <button type="button" class="satellite-delete" @click="$modal.show('satelliteDelete')"><SatIcon name="trash" :size="14" /> {{ $t("satellites.page.delete") }}</button>
      </aside>
    </div>

    <InstallCommandModal v-if="satellite" :command="satellite.install_command || ''" />
    <AgentUpdateModal :satellite="satellite" @updated="onAgentUpdateRequested" />

    <modal name="satelliteDelete" :width="520" height="auto" :scrollable="true" classes="creative-modal">
      <div class="satellite-modal text-center">
        <svg v-svg @click="$modal.hide('satelliteDelete')" class="close" symbol="modal-close" size="0 0 15 15" role="info"></svg>
        <h3 class="modal-title pt-2">{{ $t("satellites.delete_confirm") }}</h3>
        <p class="sat-muted mt-2">{{ $t("satellites.page.delete_note") }}</p>
        <div class="d-flex justify-content-center mt-4" style="gap: 10px">
          <button class="btn _bg-gray" @click="$modal.hide('satelliteDelete')">{{ $t("cancel") }}</button>
          <button class="btn _bg-green" :disabled="deleting" @click="remove">{{ $t("satellites.delete") }}</button>
        </div>
      </div>
    </modal>
  </SatellitesPage>
</template>

<script>
import { isEqual } from "lodash";
import satelliteService from "@/api/satelliteService";
import SatellitesPage from "@/components/Satellites/SatellitesPage.vue";
import SatIcon from "@/components/Satellites/SatIcon.vue";
import SatelliteStatus from "@/components/Satellites/SatelliteStatus.vue";
import ConfigStatus from "@/components/Satellites/ConfigStatus.vue";
import CpuLoadChart from "@/components/Satellites/CpuLoadChart.vue";
import GroupSelect from "@/components/Satellites/GroupSelect.vue";
import SettingsForm from "@/components/Satellites/SettingsForm.vue";
import SaveBar from "@/components/Satellites/SaveBar.vue";
import DomainList from "@/components/Satellites/DomainList.vue";
import InstallCommandModal from "@/components/Satellites/InstallCommandModal.vue";
import AgentUpdateBadge from "@/components/Satellites/AgentUpdateBadge.vue";
import AgentUpdateModal from "@/components/Satellites/AgentUpdateModal.vue";
import { canRequestAgentUpdate } from "@/components/Satellites/agentUpdate";
import satelliteFeatureGuard from "@/components/Satellites/satelliteFeatureGuard";
import { formatMixin, isOk, messagesOf } from "@/components/Satellites/format";
import { confirmLeave } from "@/components/Satellites/unsaved";
import { validateSetting } from "@/components/Satellites/settingsSchema";

const UPDATE_POLL_INTERVAL = 10000;

export default {
  name: "SatellitePage",
  components: {
    SatellitesPage,
    SatIcon,
    SatelliteStatus,
    ConfigStatus,
    CpuLoadChart,
    GroupSelect,
    SettingsForm,
    SaveBar,
    DomainList,
    InstallCommandModal,
    AgentUpdateBadge,
    AgentUpdateModal,
  },
  mixins: [formatMixin],
  beforeRouteEnter: satelliteFeatureGuard,
  beforeRouteLeave(to, from, next) {
    confirmLeave(this, next);
  },
  data() {
    return {
      loading: true,
      saving: false,
      deleting: false,
      copied: false,
      satellite: null,
      settings: {},
      domains: [],
      groups: [],
      definitions: [],
      // Settings of the groups by id, for the read-only view (loaded when another group is picked)
      groupSettings: {},
      form: { name: "", groupId: null, settings: {} },
      violations: {},
      // Refreshes the satellite while its calypso update is running
      updatePollTimer: null,
    };
  },
  computed: {
    title() {
      return this.satellite ? this.satName(this.satellite) : "";
    },
    initialForm() {
      if (!this.satellite) return null;
      return { name: this.satellite.name || "", groupId: this.satellite.group ? this.satellite.group.id : null, settings: this.settings };
    },
    dirty() {
      return !!this.initialForm && !isEqual(this.normalizedForm(), this.normalizedForm(this.initialForm));
    },
    invalid() {
      return !this.form.groupId && this.definitions.some((d) => validateSetting(d, d.key in this.form.settings ? this.form.settings[d.key] : d.default));
    },
    currentGroupId() {
      return this.satellite && this.satellite.group ? this.satellite.group.id : null;
    },
    // What happens on save when the group is changed
    groupChangeNote() {
      const from = this.currentGroupId;
      const to = this.form.groupId;
      if (from === to) return null;
      if (!from) return { warn: true, text: this.$t("satellites.page.join_note", { group: this.groupName(to) }) };
      if (!to) return { warn: false, text: this.$t("satellites.page.leave_note", { group: this.groupName(from) }) };
      return { warn: true, text: this.$t("satellites.page.move_note", { group: this.groupName(to) }) };
    },
  },
  watch: {
    "form.groupId"(id) {
      if (id) this.loadGroupSettings(id);
      // Leaving the group keeps its settings: the form starts from them, as the backend copies them.
      if (!id && this.currentGroupId) this.form.settings = { ...this.settings };
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    clearInterval(this.updatePollTimer);
  },
  methods: {
    canRequestAgentUpdate,
    onAgentUpdateRequested(satellite) {
      this.satellite = satellite;
      this.watchAgentUpdate();
    },
    // Only the satellite is refreshed, so the form being edited stays as it is.
    watchAgentUpdate() {
      clearInterval(this.updatePollTimer);
      this.updatePollTimer = null;
      if (!this.satellite || !this.satellite.agent_update || this.satellite.agent_update.status !== "pending") return;
      this.updatePollTimer = setInterval(() => {
        satelliteService.getSatellite(this.satellite.id).then((res) => {
          if (!isOk(res) || !this.satellite) return;
          this.satellite = res.data.satellite;
          if (this.satellite.agent_update.status !== "pending") this.watchAgentUpdate();
        });
      }, UPDATE_POLL_INTERVAL);
    },
    load() {
      return Promise.all([satelliteService.getSatellite(this.$route.params.id), satelliteService.getGroups(), satelliteService.getSettingsSchema()])
        .then(([detail, groups, schema]) => {
          if (!isOk(detail)) {
            if (detail && detail.status === 404) this.$router.replace({ name: "Satellites" }).catch(() => {});
            else this.showError(detail);
            return;
          }
          if (isOk(groups)) this.groups = groups.data || [];
          if (isOk(schema)) this.definitions = schema.data.definitions || [];
          this.apply(detail.data);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    apply(data) {
      this.satellite = data.satellite;
      this.settings = { ...data.settings };
      this.domains = data.domains || [];
      if (data.satellite.group) this.$set(this.groupSettings, data.satellite.group.id, { ...data.settings });
      this.reset();
      this.watchAgentUpdate();
    },
    reset() {
      this.form = { ...this.initialForm, settings: { ...this.settings } };
      this.violations = {};
    },
    normalizedForm(form = this.form) {
      return { name: (form.name || "").trim(), groupId: form.groupId, settings: form.groupId ? null : form.settings };
    },
    groupName(id) {
      const group = this.groups.find((g) => g.id === id);
      return group ? group.name : "";
    },
    loadGroupSettings(id) {
      if (this.groupSettings[id]) return;
      satelliteService.getGroup(id).then((res) => {
        if (isOk(res)) this.$set(this.groupSettings, id, res.data.settings);
      });
    },
    violation(key) {
      return messagesOf(this.violations[key]);
    },
    save() {
      if (this.saving || !this.dirty) return;
      const body = {};
      const form = this.normalizedForm();
      const initial = this.normalizedForm(this.initialForm);
      if (form.name !== initial.name) body.name = form.name || null;
      if (form.groupId !== initial.groupId) body.group_id = form.groupId;
      if (!form.groupId && !isEqual(form.settings, initial.settings)) body.settings = form.settings;

      this.saving = true;
      this.violations = {};
      satelliteService
        .updateSatellite(this.satellite.id, body)
        .then((res) => {
          if (isOk(res)) {
            this.apply(res.data);
            this.$alert({ type: "success", title: this.$t("satellites.page.saved") });
          } else if (res && res.status === 422 && res.data && res.data.violations) {
            this.violations = res.data.violations;
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    copy(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.copied = true;
        setTimeout(() => (this.copied = false), 1500);
      });
    },
    remove() {
      if (this.deleting) return;
      this.deleting = true;
      satelliteService
        .deleteSatellite(this.satellite.id)
        .then((res) => {
          if (isOk(res)) {
            this.$modal.hide("satelliteDelete");
            this.$alert({ type: "success", title: this.$t("satellites.deleted") });
            this.satellite = null;
            this.$router.push({ name: "Satellites" }).catch(() => {});
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.satellite-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 20px;
  align-items: start;

  @media (max-width: 1199px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.satellite-main {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

.satellite-general {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 20px;
  margin-top: 14px;

  .sat-field + .sat-field {
    margin-top: 0;
  }

  input {
    width: 100%;
  }

  @media (max-width: 767px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.satellite-manage {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 14px;
  font-size: 14px;
}

.satellite-side {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.satellite-facts {
  margin: 12px 0 0;

  > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 36px;
    padding: 6px 0;
    border-bottom: 1px solid var(--sat-border);

    &:last-child {
      border-bottom: 0;
    }
  }

  dt {
    font-size: 13px;
    font-weight: 500;
    color: var(--sat-muted);
  }

  dd {
    display: flex;
    align-items: center;
    gap: 4px;
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    text-align: right;
    color: var(--sat-text);
    word-break: break-all;
  }
}

.satellite-copy {
  width: 26px;
  height: 26px;
}

.satellite-delete {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  padding: 6px 4px;
  border: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--sat-muted);
  background: transparent;
  cursor: pointer;

  &:hover {
    color: var(--sat-danger);
  }

  &:focus {
    outline: none;
  }
}

.satellite-modal {
  position: relative;
  padding: 16px 24px 24px;
}
</style>
